

import React, { useState, useRef, useEffect } from "react";
import {
  BadgeCheck, Download, Share2, Scan, Users, Gift, Linkedin,
  Twitter, Link, X, Building, Image as ImageIcon
} from "lucide-react";
import html2canvas from "html2canvas";

const colorSchemes = [
  {
    primary: "#fff",
    secondary: "#0284C7",
    accent: "#0369A1",
    border: "#38BDF8",
    text: "#075985",
  },
  {
    primary: "#fff",
    secondary: "#DC2626",
    accent: "#991B1B",
    border: "#F87171",
    text: "#7F1D1D",
  },
  {
    primary: "#fff",
    secondary: "#2563EB",
    accent: "#1D4ED8",
    border: "#60A5FA",
    text: "#1E3A8A",
  },
  {
    primary: "#fff",
    secondary: "#7C3AED",
    accent: "#5B21B6",
    border: "#A78BFA",
    text: "#4C1D95",
  },
  {
    primary: "#fff",
    secondary: "#059669",
    accent: "#047857",
    border: "#34D399",
    text: "#065F46",
  },
];

const ShareModal = ({ isOpen, onClose, cardRef, profile }) => {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pngBlob, setPngBlob] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateImage = async () => {
    if (!cardRef.current || isGenerating) return;
    
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: null,
      });

      return canvas;
    } catch (error) {
      console.error("Error generating image:", error);
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const generateAndDownloadPNG = async () => {
    const canvas = await generateImage();
    if (!canvas) return;

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
    setPngBlob(blob);

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${profile.firstName}-${profile.lastName}-alma-card.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const generatePDF = async () => {
    const canvas = await generateImage();
    if (!canvas) return;

    try {
      const { jsPDF } = await import("jspdf");
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [400, 500],
      });

      pdf.addImage(imgData, "PNG", 0, 0, 400, 500);
      const blob = pdf.output("blob");
      setPdfBlob(blob);

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${profile.firstName}-${profile.lastName}-alma-card.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const shareToSocial = async (platform) => {
    if (!pngBlob) {
      const canvas = await generateImage();
      if (canvas) {
        const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
        setPngBlob(blob);
      }
    }

    const text = `Check out my Alma Card from ${profile.academicInfo?.department}!`;
    const url = platform === 'linkedin' 
      ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(text)}`
      : `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.href)}`;
    
    window.open(url, "_blank");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Share Your Card</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={generatePDF}
              disabled={isGenerating}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              PDF
            </button>
            <button
              onClick={generateAndDownloadPNG}
              disabled={isGenerating}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <ImageIcon className="h-4 w-4" />
              PNG
            </button>
          </div>
          <button
            onClick={() => shareToSocial('linkedin')}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Linkedin className="h-4 w-4" />
            Share on LinkedIn
          </button>
          <button
            onClick={() => shareToSocial('twitter')}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600"
          >
            <Twitter className="h-4 w-4" />
            Share on Twitter
          </button>
          <button
            onClick={() => {
              if (pngBlob) {
                navigator.clipboard.write([
                  new ClipboardItem({
                    'image/png': pngBlob
                  })
                ]).catch(err => {
                  console.error("Error copying to clipboard:", err);
                  alert("Unable to copy image directly. Please use the download button.");
                });
              }
            }}
            className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Link className="h-4 w-4" />
            Copy to Clipboard
          </button>
        </div>
      </div>
    </div>
  );
};

const AlmaCardPage = () => {
  const [currentScheme, setCurrentScheme] = useState(colorSchemes[0]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const fetchCurrentAlumni = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/alumni/profile`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch alumni details");
        const data = await response.json();
        setProfile(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching alumni details:", err);
        setError("Failed to load your profile. Please try again later.");
        setLoading(false);
      }
    };

    fetchCurrentAlumni();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header Section */}
        <div className="mb-8 lg:mb-12 text-center">
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Your Digital Identity Card
          </h1>
          <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">
            Access exclusive alumni benefits, attend premium events, and stay
            connected with your alma mater
          </p>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Column */}
          <div className="order-2 lg:order-1 space-y-8">
            {/* Color Schemes */}
            <div className="p-6 bg-white rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">
                Choose Your Card Theme
              </h3>
              <div className="flex gap-3 justify-center lg:justify-start">
                {colorSchemes.map((scheme, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentScheme(scheme)}
                    className="w-8 h-8 rounded-full transition-transform hover:scale-110 ring-2 ring-offset-2"
                    style={{
                      background: `linear-gradient(135deg, ${scheme.secondary}, ${scheme.accent})`,
                      ringColor: currentScheme === scheme ? scheme.secondary : "transparent",
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Benefits Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-6 bg-white rounded-xl shadow-sm">
                <Share2 className="w-8 h-8 text-purple-600 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Social Connect
                </h3>
                <p className="text-slate-600">
                  Share and network on LinkedIn and Twitter
                </p>
              </div>

              <div className="p-6 bg-white rounded-xl shadow-sm">
                <Users className="w-8 h-8 text-blue-600 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Alumni Network
                </h3>
                <p className="text-slate-600">
                  Connect with fellow alumni worldwide
                </p>
              </div>

              <div className="p-6 bg-white rounded-xl shadow-sm">
                <Scan className="w-8 h-8 text-green-600 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Event Access
                </h3>
                <p className="text-slate-600">
                  Quick entry to alumni events via QR code
                </p>
              </div>

              <div className="p-6 bg-white rounded-xl shadow-sm">
                <Gift className="w-8 h-8 text-red-600 mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Member Perks
                </h3>
                <p className="text-slate-600">
                  Exclusive alumni benefits and offers
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Card Display */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-8">
            {/* Card Component */}
            <div
  ref={cardRef}
  className="w-full max-w-[400px] h-[500px] mx-auto relative rounded-xl overflow-hidden bg-white"
  style={{
    boxShadow: `0 4px 6px -1px ${currentScheme.secondary}20, 0 2px 4px -2px ${currentScheme.secondary}10`,
    // Force aspect ratio on mobile while maintaining dimensions for export
    aspectRatio: '4/5',
  }}
>
              {/* Top Color Bar */}
              <div
                className="h-24"
                style={{
                  background: `linear-gradient(90deg, ${currentScheme.secondary}, ${currentScheme.accent})`,
                }}
              />

              <div className="relative px-6 -mt-16">
                {/* Header with Logos */}
                <div className="bg-white rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center">
                    <img
                      src="https://i.ibb.co/Y2zGN8s/saarthilogo.png"
                      alt="Saarthi"
                      className="h-12 object-contain"
                    />
                    <img
                      src={profile.college?.logo || "https://i.ibb.co/7vZw4Vp/nitk-logo.png"}
                      alt="College Logo"
                      className="h-12 w-12 object-contain"
                    />
                  </div>
                </div>

                {/* Profile Info */}
                <div className="flex gap-4">
                  {profile.personalInfo?.avatar ? (
                    <img
                      src={profile.personalInfo.avatar}
                      alt={`${profile.firstName} ${profile.lastName}`}
                      className="w-28 h-28 rounded-lg object-cover ring-2"
                      style={{ ringColor: currentScheme.border }}
                    />
                  ) : (
                    <div
                    className="w-28 h-28 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `${currentScheme.secondary}10`,
                      color: currentScheme.text,
                    }}
                  >
                    <span className="text-3xl font-bold">
                      {profile.firstName[0]}
                      {profile.lastName[0]}
                    </span>
                  </div>
                )}

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2
                      className="text-2xl font-bold tracking-tight"
                      style={{ color: currentScheme.text }}
                    >
                      {profile.firstName} {profile.lastName}
                    </h2>
                    {profile.isVerified && (
                      <BadgeCheck
                        className="h-5 w-5"
                        style={{ color: currentScheme.secondary }}
                      />
                    )}
                  </div>
                  <div
                    className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm"
                    style={{
                      backgroundColor: `${currentScheme.secondary}10`,
                      color: currentScheme.text,
                    }}
                  >
                    Class of {profile.graduationYear}
                  </div>
                  <div className="mt-4">
                    <p className="text-gray-600">{profile.academicInfo.degree}</p>
                    <p className="text-gray-500">{profile.academicInfo.department}</p>
                  </div>
                </div>
              </div>

            

              {/* Footer */}
             
            </div>

            {/* Bottom Color Bar */}
            {/* Update footer positioning */}
      <div className="absolute bottom-8 left-6 right-6">
        <div
          className="my-6 h-px w-full"
          style={{ backgroundColor: `${currentScheme.secondary}20` }}
        />
        
        <div className="flex justify-between items-end">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${currentScheme.secondary}10` }}
          >
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(
                window.location.origin + "/alumni/" + profile._id
              )}&bgcolor=FFFFFF&color=${encodeURIComponent(
                currentScheme.text.substring(1)
              )}`}
              alt="Profile QR"
              className="w-20 h-20"
            />
          </div>
          <div className="text-right">
            <span
              className="text-sm font-medium block mb-1"
              style={{ color: currentScheme.text }}
            >
              Alumni ID
            </span>
            <span className="font-mono text-gray-600">
              {profile.enrollmentNumber}
            </span>
          </div>
        </div>
      </div>
  

  {/* Bottom Color Bar - Make it stick to bottom */}
  <div
    className="absolute bottom-0 left-0 right-0 h-6"
    style={{
      background: `linear-gradient(90deg, ${currentScheme.secondary}, ${currentScheme.accent})`,
    }}
  />
</div>
          {/* Card Actions */}
          <div className="mt-6 flex justify-center gap-4">
            <button
              onClick={() => setIsShareModalOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Card
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Share Modal */}
    <ShareModal
      isOpen={isShareModalOpen}
      onClose={() => setIsShareModalOpen(false)}
      cardRef={cardRef}
      profile={profile}
    />
  </div>
  
);
};

export default AlmaCardPage;